/** Honest Jobs UI: isolate never runs rsci; queued work waits on a job runner. */

import { parseSiteInstant, parseSiteInstantMs } from "./site-instant.ts";

export const STALE_QUEUED_MS = 5 * 60 * 1000;

/** End-user stale-queue title (no drain-script / GHA jargon). */
export const STALE_QUEUED_TITLE = "Still processing";

/** Primary CTA body for jobs that stay queued too long. */
export const STALE_QUEUED_BODY =
  "Processing usually takes a few minutes. If this looks stuck, open the job and Retry, or refresh this page.";

/** One technical line for power users — keep secondary to the user CTA. */
export const STALE_QUEUED_TECH =
  "Worker isolate does not run rsci — a job runner must pick these up.";

export const JOBS_FOOTER =
  "Import, circuit diff, and findings jobs enqueue here. Processing usually takes a few minutes.";

export const IMPORT_STARTED = "Import started";

export const IMPORT_WAITING =
  "Still processing — refresh to check again. Processing usually takes a few minutes.";

export const IMPORT_COMPLETE = "Import complete — revision ready.";

export function findingsWaitingCopy(state: string): string {
  return `agent_findings ${state} — still processing. Refresh this page or open Jobs to retry. Worker isolate does not auto-succeed.`;
}

export const JOB_USER_TIMEOUT =
  "This job timed out. Retry it, or refresh this page.";

const OPS_JOB_ERROR =
  /rscircuit-job-runner|JobRunnerContainer|--containers-rollout|containers rolled out|Queue consumer claimed/i;

/** Strip operator deploy runbooks from job.error before showing them in the UI. */
export function userFacingJobError(error: string | null | undefined): string | null {
  if (error == null) return null;
  const raw = error.trim();
  if (!raw) return null;
  if (OPS_JOB_ERROR.test(raw) || /^timed out:/i.test(raw)) return JOB_USER_TIMEOUT;
  return raw;
}

/** Started column: viewer-local timezone, not raw UTC. */
export function formatJobStartedAt(
  raw: string | null | undefined,
  locales?: Intl.LocalesArgument,
  timeZone?: string,
): string {
  const date = parseSiteInstant(raw);
  if (!date) return raw?.trim() || "—";
  return date.toLocaleString(locales, {
    dateStyle: "medium",
    timeStyle: "short",
    ...(timeZone ? { timeZone } : {}),
  });
}

export function queuedAgeMs(createdAt: string | null | undefined, nowMs: number): number | null {
  const t = parseSiteInstantMs(createdAt);
  if (t == null) return null;
  return Math.max(0, nowMs - t);
}

export function staleQueuedJobs<T extends { state: string; created_at?: string | null }>(
  jobs: T[],
  nowMs: number,
  thresholdMs = STALE_QUEUED_MS,
): T[] {
  return jobs.filter((j) => j.state === "queued" && (queuedAgeMs(j.created_at, nowMs) ?? 0) >= thresholdMs);
}

export type JobActivity = {
  queued: number;
  running: number;
  active: number;
  /** Honest aria / tooltip — never says "running" when only queued. */
  ariaLabel: string;
  /** Count shown on the badge (queued + running). */
  badgeCount: number;
};

/** Topbar Jobs badge counts — queued is not "running". */
export function summarizeJobActivity(jobs: ReadonlyArray<{ state: string }>): JobActivity {
  let queued = 0;
  let running = 0;
  for (const j of jobs) {
    if (j.state === "queued") queued += 1;
    else if (j.state === "running") running += 1;
  }
  const active = queued + running;
  let ariaLabel = "Jobs";
  if (active > 0) {
    const parts: string[] = [];
    if (queued > 0) parts.push(`${queued} queued`);
    if (running > 0) parts.push(`${running} running`);
    ariaLabel = `Jobs, ${parts.join(", ")}`;
  }
  return { queued, running, active, ariaLabel, badgeCount: active };
}
